import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Seo } from "@/seo/Seo";
import { PageHero } from "@/layouts/PageHero";
import { Section } from "@/components/common/Section";
import { Container } from "@/components/common/Container";
import { SectionHeading } from "@/components/common/SectionHeading";
import { CtaButton } from "@/components/common/CtaButton";
import { FaqAccordion } from "@/components/detail/FaqAccordion";
import { DetailCta } from "@/components/detail/DetailCta";
import { solutionDetails } from "@/data/solutionDetails";
import { fadeUp, staggerContainer, viewportOnce } from "@/animations/motion";
import { ArrowUpRight } from "@/icons";
import { siteConfig } from "@/config/site.config";
import type { SolutionDetail } from "@/types/detail";
import MarketingShowcase from "@/components/common/MarketingShowcase";
import PlatformComparison from "@/components/solutions/PlatformComparison";
import TransformationJourney from "@/components/solutions/TransformationJourney";

const solutionsFaqs = [
  { question: "What's the difference between a service and a solution?", answer: "A service is a capability (like engineering or design). A solution is a complete, purpose-built product for a business function — an ERP, CRM or portal — assembled from those services." },
  { question: "Are these ready-made products?", answer: "They're proven blueprints, not rigid packages. We start from a strong foundation and tailor it to your exact processes and data." },
  { question: "How long does a solution take to launch?", answer: "We deliver in modules, so a first useful release is typically live within a few months rather than after a single long project." },
  { question: "Can you integrate with our existing systems?", answer: "Yes. Every solution is designed to connect with the tools, data and systems you already rely on." },
  { question: "Do we own what you build?", answer: "Yes — you own the code, data and roadmap, with documentation for your team." },
  { question: "Can a solution grow with us?", answer: "Absolutely. Modular architecture means you can add capabilities as your needs evolve." },
];

/** Group solutions by business domain, preserving first-seen order. */
function groupByDomain(items: SolutionDetail[]): [string, SolutionDetail[]][] {
  const map = new Map<string, SolutionDetail[]>();
  for (const item of items) {
    if (!map.has(item.domain)) map.set(item.domain, []);
    map.get(item.domain)!.push(item);
  }
  return Array.from(map.entries());
}

export default function Solutions() {
  const groups = groupByDomain(solutionDetails);

  return (
    <>
      <Seo
        title="Business Solutions"
        description="Complete, custom business solutions from VibeWell — ERP, CRM, asset & inventory management, hospital, school, restaurant, HRMS, visitor management and customer portals."
        path="/solutions"
      />
      <PageHero
        eyebrow="Business Solutions"
        titleLines={["Business Solutions ", "That Solve Real Operational Challenges."]}
        description="Whether you're looking to streamline operations, improve customer relationships or automate complex workflows, our solutions are designed to solve practical business challenges and support long-term growth."
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <CtaButton to={siteConfig.primaryCta.path} withArrow magnetic>
            Book a Free Consultation
          </CtaButton>
          <CtaButton to="/services" variant="outline">
            Explore Services
          </CtaButton>
        </div>
      </PageHero>

      <MarketingShowcase
        eyebrow="Business Solutions"

        title="AI-Powered Solutions Designed Around Your Business"

        description="
            Every organisation has unique processes, challenges and goals.
            We design intelligent software solutions that automate operations,
            connect enterprise systems and enable data-driven decision making."

        bullets={[
            "Enterprise AI solutions",
            "Workflow automation",
            "Modern web & mobile platforms",
            "Cloud-native scalable architecture",
        ]}

        image={"/images/seo/solutions-og.webp"}

        imagePosition="left"

        imageAlt="VibeWell Business Solutions"

        accent="#0F8B8D"

        primaryCta={{
            label: "Discuss Your Project",
            href: "/contact",
        }}

        secondaryCta={{
            label: "Explore Industries",
            href: "/industries",
        }}
      />

      {/* Grouped by domain */}
      {groups.map(([domain, items], gi) => (
        <Section
          key={domain}
          spacing="compact"
          className={gi % 2 === 1 ? "bg-card/40" : undefined}
        >
          <Container>
            <div className="mb-8 flex items-center gap-3">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              <h2 className="font-heading text-sm font-semibold uppercase tracking-[0.16em] text-primary">
                {domain}
              </h2>
              <span className="h-px flex-1 bg-border" />
            </div>
            <motion.div
              variants={staggerContainer(0.05, 0.03)}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            >
              {items.map((s) => (
                <motion.div key={s.slug} variants={fadeUp} data-testid={`solutions-card-${s.slug}`}>
                  <Link
                    to={`/solutions/${s.slug}`}
                    className="group flex h-full flex-col rounded-[1.25rem] border border-border bg-card p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-accent/30 hover:shadow-[0_24px_60px_-28px_rgba(6,182,212,0.35)]"
                  >
                    <span
                      className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white"
                      style={{ background: s.accent }}
                    >
                      <s.icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 font-heading text-lg font-semibold text-foreground">
                      {s.title}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                      {s.tagline}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-all duration-300 group-hover:gap-2.5">
                      View solution <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </Container>
        </Section>
      ))}

      <TransformationJourney />

      {/* Comparison */}
      <PlatformComparison />
      {/* <Section spacing="compact">
        <Container>
          <SectionHeading
            eyebrow="Why custom"
            title="Traditional Software vs Intelligent Business Platform"
            description="Move beyond disconnected applications and manual processes with an AI-powered enterprise platform."
          />
          <div className="mt-10">
            <ServiceComparisonTable rows={SHARED_COMPARISON} />
          </div>
        </Container>
      </Section> */}

      {/* FAQ */}
      <Section spacing="compact" className="bg-card/40">
        <Container>
          <SectionHeading eyebrow="FAQ" title="Frequently asked questions" align="center" />
          <div className="mt-10">
            <FaqAccordion faqs={solutionsFaqs} />
          </div>
        </Container>
      </Section>

      <DetailCta />
    </>
  );
}
